/**
 * Row for a session.
 */
import * as React from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Calendar, Clock, Video, FileEdit, Eye, Users } from 'lucide-react';
import { Button } from '@/components/forms/Button';
import { VisioPopupBlockedModal } from '@/components/common/modals/VisioPopupBlockedModal';
import { LabelBadge } from '@/components/common/LabelBadge';
import { TableRow } from '@/components/common/data-table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  PAIR_ONE_OFF_BADGE_CONFIG,
  SESSION_REMOTE_STATUS_CONFIG,
  SESSION_REMOTE_TYPE_CONFIG,
  SESSION_RECURRENCE_CONFIG,
  JOINABLE_SESSION_STATUSES,
} from '@/components/sessions/session-configs';
import type { SessionTab, ListItem } from '@/types/remote/session.types';
import { useJoinRemoteMeeting } from '@/hooks/useJoinRemoteMeeting';
import { isPairOneOffSession } from '@/components/sessions/remote/session-form-utils';
import {
  formatTime,
  calcDurationMinutes,
  parseSessionDate,
  isSessionTerminated,
  getSessionReportState,
  getCanViewReport,
  deriveSessionParticipants,
  getEditTooltip,
  getCancelTooltip,
  getReportTooltip,
  useNavigateToRemoteReport,
  type SessionItemPageActions,
} from './session-table-utils';

/** Props for {@link SessionRow}. */
interface SessionRowProps {
  session: ListItem;
  activeTab: SessionTab;
  onEdit?: (sessionId: string) => void;
  onView?: (sessionId: string) => void;
  onCancel?: (sessionId: string) => void;
  pageActions?: SessionItemPageActions;
}

/** Minimal person shape for participant cells (name + overflow tooltip). */
type RowPerson = {
  id?: string | null;
  user?: { id?: string; firstName?: string | null; lastName?: string | null } | null;
};

/** Single student name, or a count with a tooltip listing everyone. */
function renderStudentsRow(students: RowPerson[]): React.ReactNode {
  const count = students.length;
  if (count === 0) return <span className="text-muted-foreground">—</span>;
  if (count === 1) {
    return (
      <div className="flex items-center gap-2">
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium"
          style={{ backgroundColor: 'var(--color-2-nuance-1)' }}
        >
          {students[0].user?.firstName?.[0] || 'E'}
        </div>
        <span className="text-foreground">
          {students[0].user?.firstName} {students[0].user?.lastName}
        </span>
      </div>
    );
  }
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-1.5 text-foreground cursor-default">
            <Users className="w-3.5 h-3.5 text-[var(--color-1-nuance-1)] shrink-0" />
            <span className="text-sm">
              {count} élève{count > 1 ? 's' : ''}
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <div className="space-y-0.5">
            {students.map((s) => (
              <p key={s.id ?? s.user?.id}>
                {s.user?.firstName} {s.user?.lastName}
              </p>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/** Single animator name, or a count with a tooltip listing everyone. */
function renderAnimatorsRow(animators: RowPerson[]): React.ReactNode {
  const count = animators.length;
  if (count === 0) {
    return (
      <div className="flex items-center gap-1.5">
        <Users className="w-3.5 h-3.5 text-[var(--color-1-nuance-1)] shrink-0" />
        <span className="text-sm">0 animateur</span>
      </div>
    );
  }
  if (count === 1) {
    return (
      <div className="flex items-center gap-2">
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium"
          style={{ backgroundColor: 'var(--color-3-nuance-1)' }}
        >
          {animators[0].user?.firstName?.[0] || 'A'}
        </div>
        <span className="text-foreground">
          {animators[0].user?.firstName} {animators[0].user?.lastName}
        </span>
      </div>
    );
  }
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-1.5 text-foreground cursor-default">
            <Users className="w-3.5 h-3.5 text-[var(--color-1-nuance-1)] shrink-0" />
            <span className="text-sm">
              {count} animateur{count > 1 ? 's' : ''}
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <div className="space-y-0.5">
            {animators.map((a) => (
              <p key={a.id ?? a.user?.id}>
                {a.user?.firstName} {a.user?.lastName}
              </p>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/** Welcome-meeting cells: participants, then students, then parents. */
function renderWelcomeRow(
  welcomeParticipants: RowPerson[],
  students: RowPerson[],
  parents: RowPerson[],
): React.ReactNode {
  const count = welcomeParticipants.length;
  if (count === 0) {
    return (
      <div className="flex items-center gap-1.5">
        <Users className="w-3.5 h-3.5 text-[var(--color-1-nuance-1)] shrink-0" />
        <span className="text-sm">0 participant</span>
      </div>
    );
  }
  if (count === 1) {
    return (
      <span className="text-foreground text-sm">
        {welcomeParticipants[0].user?.firstName} {welcomeParticipants[0].user?.lastName}
      </span>
    );
  }
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-1.5 text-foreground cursor-default">
            <Users className="w-3.5 h-3.5 text-[var(--color-1-nuance-1)] shrink-0" />
            <span className="text-sm">
              {count} participant{count > 1 ? 's' : ''}
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <div className="space-y-0.5">
            {students.map((s) => (
              <p key={s.id ?? s.user?.id}>
                {s.user?.firstName} {s.user?.lastName}
                <span className="text-muted-foreground ml-1 text-[10px]">(élève)</span>
              </p>
            ))}
            {parents.map((p) => (
              <p key={p.id ?? p.user?.id}>
                {p.user?.firstName} {p.user?.lastName}
                <span className="text-muted-foreground ml-1 text-[10px]">(parent)</span>
              </p>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/** Open the report editor for this session (creates a draft when `reportId` is missing). */
function editReport(
  navigateToReport: (id: string, type: string, reportId?: string | null) => Promise<void>,
  sessionId: string,
  sessionType: string,
  reportId?: string | null,
): void {
  void navigateToReport(sessionId, sessionType, reportId);
}

/** Props for {@link renderJoinMeetingButtonRow}. */
interface JoinButtonRowProps {
  session: ListItem;
  type: string;
  canJoin: boolean;
  join: (id: string) => Promise<void>;
  isJoining: boolean;
  tooltipText: string;
}

/** Join-visio icon on a remote session row when the type is joinable. */
function renderJoinMeetingButtonRow({
  session,
  type,
  canJoin,
  join,
  isJoining,
  tooltipText,
}: JoinButtonRowProps): React.ReactNode {
  if (session.type !== type) return null;
  if (!canJoin) return null;
  if (!JOINABLE_SESSION_STATUSES.has(session.status)) return null;
  return (
    <Button
      variant="outline"
      color="rgb(var(--color-error))"
      size="icon"
      loading={isJoining}
      className={session.status === 'IN_PROGRESS' ? 'session-video-blink' : ''}
      onClick={() => void join(session.id)}
      icon={Video}
      tooltipText={tooltipText}
    />
  );
}

/** Props for {@link SessionRowParticipantCells}. */
interface SessionRowParticipantCellsProps {
  activeTab: SessionTab;
  session: ListItem;
  tutor: RowPerson | undefined;
  students: RowPerson[];
  parents: RowPerson[];
  animators: RowPerson[];
  volunteers: RowPerson[];
  welcomeParticipants: RowPerson[];
}

/** Participant cells on a remote session row, by tab. */
function SessionRowParticipantCells({
  activeTab,
  session,
  tutor,
  students,
  parents,
  animators,
  volunteers,
  welcomeParticipants,
}: Readonly<SessionRowParticipantCellsProps>): React.ReactElement {
  const typeConfig = SESSION_REMOTE_TYPE_CONFIG[session.type];
  const typeBadges = (
    <div className="inline-flex flex-col items-center gap-0.5">
      <LabelBadge
        label={typeConfig?.label ?? session.type}
        icon={typeConfig?.icon}
        variant={typeConfig?.badgeVariant ?? 'ghost'}
        color={typeConfig?.badgeColor ?? 'var(--color-2-nuance-1)'}
        size="md"
      />
      {isPairOneOffSession(session) ? (
        <LabelBadge
          label={PAIR_ONE_OFF_BADGE_CONFIG.label}
          icon={PAIR_ONE_OFF_BADGE_CONFIG.icon}
          variant={PAIR_ONE_OFF_BADGE_CONFIG.badgeVariant}
          color={PAIR_ONE_OFF_BADGE_CONFIG.badgeColor}
          size="sm"
        />
      ) : null}
      {session.recurrenceType &&
      session.recurrenceType !== 'none' &&
      session.recurrenceType !== 'once' ? (
        <LabelBadge
          label={SESSION_RECURRENCE_CONFIG[session.recurrenceType]?.label ?? 'Récurrente'}
          icon={SESSION_RECURRENCE_CONFIG[session.recurrenceType]?.icon}
          variant={SESSION_RECURRENCE_CONFIG[session.recurrenceType]?.badgeVariant ?? 'outlined'}
          color={
            SESSION_RECURRENCE_CONFIG[session.recurrenceType]?.badgeColor ??
            'var(--color-2-nuance-1)'
          }
          size="sm"
        />
      ) : null}
    </div>
  );

  return (
    <>
      {activeTab === 'tutoring' && (
        <>
          <td>
            {tutor ? (
              <div className="flex items-center gap-2">
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium"
                  style={{ backgroundColor: 'var(--color-1-nuance-1)' }}
                >
                  {tutor.user?.firstName?.[0] || 'T'}
                </div>
                <span className="text-foreground">
                  {tutor.user?.firstName} {tutor.user?.lastName}
                </span>
              </div>
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </td>
          <td>{renderStudentsRow(students)}</td>
        </>
      )}

      {(activeTab === 'information-meeting' ||
        activeTab === 'welcome-meeting' ||
        activeTab === 'field-event') && <td>{renderAnimatorsRow(animators)}</td>}

      {activeTab === 'information-meeting' && (
        <td>
          {volunteers.length === 0 ? (
            <div className="flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5 text-[var(--color-1-nuance-1)] shrink-0" />
              <span className="text-sm">0 participant</span>
            </div>
          ) : (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-1.5 text-foreground cursor-default">
                    <Users className="w-3.5 h-3.5 text-[var(--color-1-nuance-1)] shrink-0" />
                    <span className="text-sm">
                      {volunteers.length} participant{volunteers.length > 1 ? 's' : ''}
                    </span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="space-y-0.5">
                    {volunteers.map((v) => (
                      <p key={v.id ?? v.user?.id}>
                        {v.user?.firstName} {v.user?.lastName}
                      </p>
                    ))}
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </td>
      )}

      {activeTab === 'welcome-meeting' && (
        <td>{renderWelcomeRow(welcomeParticipants, students, parents)}</td>
      )}

      {activeTab === 'field-event' && (
        <>
          <td className="text-sm font-medium text-foreground">
            {session.durationMinutes ? `${Math.round(session.durationMinutes / 480)} j` : '1 j'}
          </td>
          <td>
            {students.length > 0 ? (
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-1.5 text-foreground cursor-default">
                      <Users className="w-3.5 h-3.5 text-[var(--color-1-nuance-1)]" />
                      <span>{students.length}</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="space-y-0.5">
                      {students.map((s) => (
                        <p key={s.id ?? s.user?.id}>
                          {s.user?.firstName} {s.user?.lastName}
                        </p>
                      ))}
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </td>
        </>
      )}

      {activeTab === 'tutoring' && <td className="text-center">{typeBadges}</td>}
    </>
  );
}

/** Props for {@link SessionRowActionsTd}. */
interface SessionRowActionsTdProps {
  session: ListItem;
  canEdit: boolean;
  canCancel: boolean;
  canJoinWelcomeMeeting: boolean;
  canJoinInformationMeeting: boolean;
  canViewReport: boolean;
  onEdit?: (id: string) => void;
  onView?: (id: string) => void;
  onCancel?: (id: string) => void;
}

/** Edit / cancel / join / report actions on a remote session row. */
function SessionRowActionsTd({
  session,
  canEdit,
  canCancel,
  canJoinWelcomeMeeting,
  canJoinInformationMeeting,
  canViewReport,
  onEdit,
  onView,
  onCancel,
}: Readonly<SessionRowActionsTdProps>): React.ReactElement {
  const { navigateToReport, isLoading: isLoadingReport } = useNavigateToRemoteReport();
  const { isJoining, join, popupBlocked } = useJoinRemoteMeeting();

  const isTerminated = isSessionTerminated(session.status);
  const { hasReport, isReportValidated, reportDisabled } = getSessionReportState(
    session,
    isLoadingReport,
  );

  const handleReportClick = () =>
    editReport(navigateToReport, session.id, session.type, session.compteRenduId);

  return (
    <td className="text-center">
      <div className="flex items-center justify-center gap-1">
        {renderJoinMeetingButtonRow({
          session,
          type: 'welcome_meeting',
          canJoin: canJoinWelcomeMeeting,
          join,
          isJoining,
          tooltipText: "Rejoindre réunion d'accueil",
        })}
        {renderJoinMeetingButtonRow({
          session,
          type: 'information_meeting',
          canJoin: canJoinInformationMeeting,
          join,
          isJoining,
          tooltipText: "Rejoindre réunion d'information",
        })}
        <VisioPopupBlockedModal {...popupBlocked} />

        {onView && <Button preset="row-view" onClick={() => onView(session.id)} />}

        {canEdit && onEdit && (
          <Button
            preset="row-edit"
            onClick={() => onEdit(session.id)}
            disabled={isTerminated}
            tooltipText={getEditTooltip(isTerminated, session.status)}
          />
        )}

        {canViewReport && (
          <Button
            variant="ghost"
            color="var(--color-3-nuance-1)"
            size="icon"
            onClick={handleReportClick}
            icon={isReportValidated ? Eye : FileEdit}
            disabled={reportDisabled}
            loading={isLoadingReport}
            tooltipText={getReportTooltip(
              isLoadingReport,
              isReportValidated,
              session.compteRenduStatus,
              hasReport,
              session.status,
            )}
          />
        )}

        {canCancel && onCancel && (
          <Button
            preset="row-cancel"
            onClick={() => onCancel(session.id)}
            disabled={isTerminated}
            tooltipText={getCancelTooltip(isTerminated, session.status)}
          />
        )}
      </div>
    </td>
  );
}

/** Row for a session. */
export const SessionRow: React.FC<SessionRowProps> = ({
  session,
  activeTab,
  onEdit,
  onView,
  onCancel,
  pageActions,
}) => {
  const canEdit = pageActions?.canEditRow ?? true;
  const canCancel = pageActions?.canCancelRow ?? true;
  const canJoinWelcomeMeeting = pageActions?.canJoinWelcomeMeeting ?? false;
  const canJoinInformationMeeting = pageActions?.canJoinInformationMeeting ?? false;
  const canViewReport = getCanViewReport(session.type, pageActions);

  const statusConfig =
    SESSION_REMOTE_STATUS_CONFIG[session.status] || SESSION_REMOTE_STATUS_CONFIG.PLANNED;
  const StatusIcon = statusConfig?.icon;

  const parsedDate =
    session.sessionDate instanceof Date
      ? session.sessionDate
      : parseSessionDate(String(session.sessionDate ?? ''));
  const { tutor, students, parents, animators, volunteers, welcomeParticipants } =
    deriveSessionParticipants(session);

  const formattedDate = format(parsedDate, 'd MMM', { locale: fr });
  const formattedYear = format(parsedDate, 'yyyy', { locale: fr });
  const formattedDay = format(parsedDate, 'EEE', { locale: fr });
  const typeConfig = SESSION_REMOTE_TYPE_CONFIG[session.type];
  const typeBadges = (
    <div className="inline-flex flex-col items-center gap-0.5">
      <LabelBadge
        label={typeConfig?.label ?? session.type}
        icon={typeConfig?.icon}
        variant={typeConfig?.badgeVariant ?? 'ghost'}
        color={typeConfig?.badgeColor ?? 'var(--color-2-nuance-1)'}
        size="md"
      />
      {isPairOneOffSession(session) ? (
        <LabelBadge
          label={PAIR_ONE_OFF_BADGE_CONFIG.label}
          icon={PAIR_ONE_OFF_BADGE_CONFIG.icon}
          variant={PAIR_ONE_OFF_BADGE_CONFIG.badgeVariant}
          color={PAIR_ONE_OFF_BADGE_CONFIG.badgeColor}
          size="sm"
        />
      ) : null}
      {session.recurrenceType &&
      session.recurrenceType !== 'none' &&
      session.recurrenceType !== 'once' ? (
        <LabelBadge
          label={SESSION_RECURRENCE_CONFIG[session.recurrenceType]?.label ?? 'Récurrente'}
          icon={SESSION_RECURRENCE_CONFIG[session.recurrenceType]?.icon}
          variant={SESSION_RECURRENCE_CONFIG[session.recurrenceType]?.badgeVariant ?? 'outlined'}
          color={
            SESSION_RECURRENCE_CONFIG[session.recurrenceType]?.badgeColor ??
            'var(--color-2-nuance-1)'
          }
          size="sm"
        />
      ) : null}
    </div>
  );

  return (
    <TableRow>
      <td>
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-[color-mix(in_srgb,var(--color-1-nuance-1)_10%,transparent)]">
            <Calendar className="w-4 h-4 text-[var(--color-1-nuance-1)]" />
          </div>
          <div className="flex flex-col">
            <span className="font-medium text-foreground">{formattedDate}</span>
            <span className="text-xs text-muted-foreground">{formattedDay}</span>
            <span className="text-xs font-bold" style={{ color: 'var(--accent-orange)' }}>
              {formattedYear}
            </span>
          </div>
          <div className="ml-2 flex items-center gap-1.5 text-sm text-muted-foreground">
            <Clock className="w-3.5 h-3.5 text-[var(--color-1-nuance-1)]" />
            <span>
              {formatTime(session.startTime)} (
              {session.durationMinutes ??
                calcDurationMinutes(session.startTime, session.endTime) ??
                '?'}{' '}
              min)
            </span>
          </div>
        </div>
      </td>

      {activeTab === 'all' && <td className="text-center">{typeBadges}</td>}

      <SessionRowParticipantCells
        activeTab={activeTab}
        session={session}
        tutor={tutor}
        students={students}
        parents={parents}
        animators={animators}
        volunteers={volunteers}
        welcomeParticipants={welcomeParticipants}
      />

      <td className="text-center">
        <LabelBadge
          label={statusConfig.label}
          icon={statusConfig.isInProgress ? <span className="session-dot-pulse" /> : StatusIcon}
          variant={statusConfig.badgeVariant}
          color={statusConfig.badgeColor}
          size="md"
        />
      </td>

      <SessionRowActionsTd
        session={session}
        canEdit={canEdit}
        canCancel={canCancel}
        canJoinWelcomeMeeting={canJoinWelcomeMeeting}
        canJoinInformationMeeting={canJoinInformationMeeting}
        canViewReport={canViewReport}
        onEdit={onEdit}
        onView={onView}
        onCancel={onCancel}
      />
    </TableRow>
  );
};
